/**
 * RichTextEditor - TipTap wrapper used in:
 *  - NewTicketPage description
 *  - TicketDetailCustomerPage reply composer
 *  - TicketDetailAgentPage reply / internal-note composer
 *
 * The editor produces HTML; consumers should treat the HTML output as
 * untrusted and the server should sanitize on receipt. We additionally
 * keep a plain-text projection (`getText`) for the body_text field that
 * the API requires.
 *
 * Toolbar
 * -------
 * Icons are inline SVG (not Font Awesome) so the toolbar is fully
 * visible on dev installs that have not yet wired the FA Pro kit
 * (Q4 in SPEC §19). Buttons are grouped with vertical dividers:
 *
 *   [paragraph-style] | B I U S | bullet ordered quote | link code code-block hr | undo redo
 *
 * Hidden by `editor === null` until TipTap finishes mounting so the
 * toolbar does not flash a non-functional row on first paint.
 *
 * Click target
 * ------------
 * The contenteditable receives ``min-height`` directly via
 * editorProps.attributes.style so a click ANYWHERE inside the visible
 * box lands on the editor. The wrapper additionally has cursor-text +
 * an onClick that forwards focus, as belt-and-braces for stray clicks
 * on the rounded padding band.
 */
import { mergeAttributes } from "@tiptap/core";
import Paragraph from "@tiptap/extension-paragraph";
import type { EditorView } from "@tiptap/pm/view";
import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import Underline from "@tiptap/extension-underline";
import { useEffect, useRef, useState, type SVGProps } from "react";

import { htmlToMarkdown, markdownToHtml } from "@/lib/markdown";

import { ResizableImage } from "./ResizableImage";

// ---------------------------------------------------------------------
// Markdown composition mode (developer request, 2026-06-11)
// ---------------------------------------------------------------------
// The editor offers two surfaces over the same onChange(html, text)
// contract: the TipTap WYSIWYG and a raw CommonMark textarea. The
// textarea converts to HTML on every change (lib/markdown.ts), so
// parents never know which surface authored the content. The chosen
// mode is a per-browser preference - someone who writes Markdown all
// day should not have to re-toggle on every composer.
type ComposerMode = "rich" | "markdown";

const MODE_STORAGE_KEY = "buyist-support:composer-mode";

function readPreferredMode(): ComposerMode {
  try {
    return localStorage.getItem(MODE_STORAGE_KEY) === "markdown"
      ? "markdown"
      : "rich";
  } catch {
    return "rich";
  }
}

function persistPreferredMode(mode: ComposerMode): void {
  try {
    localStorage.setItem(MODE_STORAGE_KEY, mode);
  } catch {
    /* private mode / storage full - the toggle still works for the session */
  }
}

/**
 * TightParagraph - overrides the default Paragraph node so every
 * ``<p>`` ships with an inline style that makes email-style spacing
 * work in BOTH the editor and the recipient's mail client:
 *
 *   - ``margin: 0`` removes the prose / browser-default vertical
 *     gap between consecutive paragraphs, so a single Enter looks
 *     like one line break rather than a double-spaced blank.
 *
 *   - ``min-height: 1.2em`` keeps an empty paragraph one line tall,
 *     so "Enter twice = visible blank line" continues to work even
 *     though the paragraph has no content. (A bare ``<p></p>`` with
 *     zero margin collapses to zero height otherwise.)
 *
 * We keep paragraphs as the structural unit - lists wrap the
 * containing paragraph, so each visual line MUST be its own
 * paragraph for "select 3 lines, toggle bullet list, get 3 bullets"
 * to behave correctly.
 *
 * Critical: we return the standard content hole ``0`` from
 * ``renderHTML`` and do NOT inject any extra DOM children for the
 * empty case. ProseMirror tracks cursor position from the DOM back
 * to its document model, and a phantom ``<br>`` inside an empty
 * paragraph breaks that mapping - keystrokes silently drop. The
 * inline ``min-height`` is the right way to give an empty
 * paragraph height without inventing nodes the model doesn't know
 * about.
 */
const TightParagraph = Paragraph.extend({
  renderHTML({ HTMLAttributes }) {
    const attrs = mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
      style: "margin: 0; min-height: 1.2em",
    });
    return ["p", attrs, 0];
  },
});

export interface RichTextEditorProps {
  value: string;
  onChange: (html: string, text: string) => void;
  placeholder?: string;
  minHeight?: string;
  /** Optional id used by labels / tests. */
  id?: string;
  /** ARIA label fallback when there is no visible label. */
  ariaLabel?: string;
  /** When provided, image files pasted OR dragged into the editor
   * are uploaded via this callback. The returned URL is inserted as
   * an ``<img src=URL>`` (ResizableImage) node at the current
   * selection (paste) or at the drop point (drag). Omit (or leave
   * undefined) on surfaces that should not accept inline images
   * (e.g. internal-note inline editors, customer portal composer).
   *
   * Paste and drop share the upload pipeline in
   * ``uploadAndInsertImages`` so behavior stays identical across
   * the two ingestion paths. */
  onUploadImage?: (file: File) => Promise<string>;
}

/** Shared upload-and-insert flow used by both ``handlePaste`` and
 * ``handleDrop``. Filters the incoming FileList for image MIME
 * types, uploads each one in order via ``handler``, and dispatches
 * a ProseMirror transaction that inserts an ``<img src=URL>`` node
 * at the given position (omit ``pos`` to insert at the current
 * selection - the paste case).
 *
 * Returns ``true`` when at least one image was found and the
 * pipeline took over the event; ``false`` when there was nothing
 * for us to do (so ProseMirror / the browser can fall back to
 * default behavior, e.g. inserting dropped text). The caller is
 * responsible for ``preventDefault`` / ``stopPropagation``.
 *
 * Failures are logged to ``console.error`` and the per-file insert
 * is skipped - matching the paste handler's pre-existing policy
 * that the composer page owns user-facing error toasts.
 *
 * Exported for unit testing. The runtime caller is the editor's
 * own ``handlePaste`` / ``handleDrop`` defined further down. */
export function uploadAndInsertImages(
  view: EditorView,
  files: FileList | File[] | null,
  handler: ((file: File) => Promise<string>) | undefined,
  pos?: number,
): boolean {
  if (!handler || !files) return false;
  const images = Array.from(files).filter((f) =>
    f.type.startsWith("image/"),
  );
  if (images.length === 0) return false;
  void (async () => {
    let insertAt = pos;
    for (const file of images) {
      try {
        const url = await handler(file);
        const node = view.state.schema.nodes.image?.create({
          src: url,
          alt: file.name,
        });
        if (!node) continue;
        const tr = insertAt === undefined
          ? view.state.tr.replaceSelectionWith(node, false)
          : view.state.tr.insert(insertAt, node);
        view.dispatch(tr);
        // For multi-file drops at a specific position, advance the
        // insertion point past the node we just inserted so the
        // next image lands AFTER its sibling rather than at the
        // same coords (which would render them in reverse order).
        if (insertAt !== undefined) insertAt += node.nodeSize;
      } catch (err) {
        // Surface to the browser console - the agent will see a
        // missing inline image and re-try. We deliberately do not
        // toast here; the composer page owns user feedback.
        console.error("Inline image upload failed", err);
      }
    }
  })();
  return true;
}

// -------------------------------------------------------------------
// Inline SVG icons. 18x18 stroke-only line icons - render anywhere,
// no font dependency.
// -------------------------------------------------------------------

type IconProps = SVGProps<SVGSVGElement>;

function Icon({ children, ...rest }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      width={16}
      height={16}
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}

const BoldIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6 4h8a4 4 0 0 1 0 8H6z" />
    <path d="M6 12h9a4 4 0 0 1 0 8H6z" />
  </Icon>
);
const ItalicIcon = (p: IconProps) => (
  <Icon {...p}>
    <line x1="19" y1="4" x2="10" y2="4" />
    <line x1="14" y1="20" x2="5" y2="20" />
    <line x1="15" y1="4" x2="9" y2="20" />
  </Icon>
);
const UnderlineIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6 4v8a6 6 0 0 0 12 0V4" />
    <line x1="4" y1="21" x2="20" y2="21" />
  </Icon>
);
const StrikeIcon = (p: IconProps) => (
  <Icon {...p}>
    <line x1="4" y1="12" x2="20" y2="12" />
    <path d="M16 6c-1.5-1.5-4-2-6-2-3 0-5 1.5-5 4 0 2 2 3 5 4" />
    <path d="M8 18c1.5 1.5 4 2 6 2 3 0 5-1.5 5-4" />
  </Icon>
);
const BulletListIcon = (p: IconProps) => (
  <Icon {...p}>
    <line x1="9" y1="6" x2="20" y2="6" />
    <line x1="9" y1="12" x2="20" y2="12" />
    <line x1="9" y1="18" x2="20" y2="18" />
    <circle cx="4" cy="6" r="1.4" fill="currentColor" />
    <circle cx="4" cy="12" r="1.4" fill="currentColor" />
    <circle cx="4" cy="18" r="1.4" fill="currentColor" />
  </Icon>
);
const OrderedListIcon = (p: IconProps) => (
  <Icon {...p}>
    <line x1="9" y1="6" x2="20" y2="6" />
    <line x1="9" y1="12" x2="20" y2="12" />
    <line x1="9" y1="18" x2="20" y2="18" />
    <text x="2" y="9" fontSize="6" fill="currentColor" stroke="none">1</text>
    <text x="2" y="15" fontSize="6" fill="currentColor" stroke="none">2</text>
    <text x="2" y="21" fontSize="6" fill="currentColor" stroke="none">3</text>
  </Icon>
);
const QuoteIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M3 7a3 3 0 0 1 3-3h2v6H5a2 2 0 0 0-2 2v4h5v-6" />
    <path d="M13 7a3 3 0 0 1 3-3h2v6h-3a2 2 0 0 0-2 2v4h5v-6" />
  </Icon>
);
const LinkIcon = (p: IconProps) => (
  <Icon {...p}>
    <path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07l-1 1" />
    <path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1-1" />
  </Icon>
);
const CodeIcon = (p: IconProps) => (
  <Icon {...p}>
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </Icon>
);
const CodeBlockIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <polyline points="9 9 6 12 9 15" />
    <polyline points="15 9 18 12 15 15" />
  </Icon>
);
const HrIcon = (p: IconProps) => (
  <Icon {...p}>
    <line x1="3" y1="12" x2="21" y2="12" />
  </Icon>
);
// Simplified markdown-mark: bordered box, "M" left, down-arrow right.
const MarkdownIcon = (p: IconProps) => (
  <Icon {...p}>
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M5.5 15v-5.5l2.5 2.5 2.5-2.5V15" />
    <path d="M17 9.5V15" />
    <polyline points="15 13 17 15 19 13" />
  </Icon>
);
const UndoIcon = (p: IconProps) => (
  <Icon {...p}>
    <polyline points="9 14 4 9 9 4" />
    <path d="M20 20v-7a4 4 0 0 0-4-4H4" />
  </Icon>
);
const RedoIcon = (p: IconProps) => (
  <Icon {...p}>
    <polyline points="15 14 20 9 15 4" />
    <path d="M4 20v-7a4 4 0 0 1 4-4h12" />
  </Icon>
);

// -------------------------------------------------------------------
// Toolbar buttons
// -------------------------------------------------------------------

interface ButtonProps {
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  label: string;
  children: React.ReactNode;
}

function ToolbarButton({
  active,
  disabled,
  onClick,
  label,
  children,
}: ButtonProps) {
  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      aria-label={label}
      aria-pressed={active ? "true" : undefined}
      title={label}
      disabled={disabled}
      className={`flex h-7 w-7 items-center justify-center rounded text-sm transition-colors ${
        active
          ? "bg-primary-100 text-primary-700"
          : "text-neutral-600 hover:bg-neutral-100"
      } ${disabled ? "cursor-not-allowed opacity-40" : "cursor-pointer"}`}
    >
      {children}
    </button>
  );
}

function ToolbarDivider() {
  return <div className="mx-1 h-5 w-px self-center bg-neutral-300" />;
}

function ParagraphStyleSelect({ editor }: { editor: Editor }) {
  type Value = "paragraph" | "h1" | "h2" | "h3";
  const current: Value = editor.isActive("heading", { level: 1 })
    ? "h1"
    : editor.isActive("heading", { level: 2 })
      ? "h2"
      : editor.isActive("heading", { level: 3 })
        ? "h3"
        : "paragraph";

  return (
    <select
      value={current}
      onChange={(e) => {
        const v = e.target.value as Value;
        const chain = editor.chain().focus();
        if (v === "paragraph") chain.setParagraph().run();
        else if (v === "h1") chain.setHeading({ level: 1 }).run();
        else if (v === "h2") chain.setHeading({ level: 2 }).run();
        else if (v === "h3") chain.setHeading({ level: 3 }).run();
      }}
      onMouseDown={(e) => e.stopPropagation()}
      aria-label="Paragraph style"
      className="h-7 rounded border border-neutral-200 bg-white px-1 text-xs text-neutral-700 hover:bg-neutral-100 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
    >
      <option value="paragraph">Body</option>
      <option value="h1">Heading 1</option>
      <option value="h2">Heading 2</option>
      <option value="h3">Heading 3</option>
    </select>
  );
}

interface ToolbarProps {
  editor: Editor;
  mode: ComposerMode;
  onToggleMode: () => void;
}

function Toolbar({ editor, mode, onToggleMode }: ToolbarProps) {
  if (mode === "markdown") {
    // Markdown mode: the formatting buttons would silently operate on
    // the hidden WYSIWYG document, so they go away entirely. What
    // remains is the dialect hint and the way back.
    return (
      <div className="flex flex-wrap items-center gap-1 border-b border-neutral-200 bg-neutral-50 px-2 py-1">
        <span className="px-1 text-xs text-neutral-500">
          Markdown (CommonMark + tables)
        </span>
        <div className="ml-auto flex items-center gap-1">
          <ToolbarButton
            label="Switch to rich text editor"
            active
            onClick={onToggleMode}
          >
            <MarkdownIcon />
          </ToolbarButton>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1 border-b border-neutral-200 bg-neutral-50 px-2 py-1">
      <ParagraphStyleSelect editor={editor} />
      <ToolbarDivider />

      <ToolbarButton
        label="Bold (Cmd/Ctrl + B)"
        active={editor.isActive("bold")}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <BoldIcon />
      </ToolbarButton>
      <ToolbarButton
        label="Italic (Cmd/Ctrl + I)"
        active={editor.isActive("italic")}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <ItalicIcon />
      </ToolbarButton>
      <ToolbarButton
        label="Underline (Cmd/Ctrl + U)"
        active={editor.isActive("underline")}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <UnderlineIcon />
      </ToolbarButton>
      <ToolbarButton
        label="Strikethrough"
        active={editor.isActive("strike")}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <StrikeIcon />
      </ToolbarButton>

      <ToolbarDivider />

      <ToolbarButton
        label="Bulleted list"
        active={editor.isActive("bulletList")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <BulletListIcon />
      </ToolbarButton>
      <ToolbarButton
        label="Numbered list"
        active={editor.isActive("orderedList")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <OrderedListIcon />
      </ToolbarButton>
      <ToolbarButton
        label="Block quote"
        active={editor.isActive("blockquote")}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <QuoteIcon />
      </ToolbarButton>

      <ToolbarDivider />

      <ToolbarButton
        label="Insert link"
        active={editor.isActive("link")}
        onClick={() => {
          const previous = editor.getAttributes("link").href as string | undefined;
          const url = window.prompt("Link URL", previous ?? "https://");
          if (url === null) return;
          if (url === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            return;
          }
          editor
            .chain()
            .focus()
            .extendMarkRange("link")
            .setLink({ href: url })
            .run();
        }}
      >
        <LinkIcon />
      </ToolbarButton>
      <ToolbarButton
        label="Inline code"
        active={editor.isActive("code")}
        onClick={() => editor.chain().focus().toggleCode().run()}
      >
        <CodeIcon />
      </ToolbarButton>
      <ToolbarButton
        label="Code block"
        active={editor.isActive("codeBlock")}
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
      >
        <CodeBlockIcon />
      </ToolbarButton>
      <ToolbarButton
        label="Horizontal rule"
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
      >
        <HrIcon />
      </ToolbarButton>

      <div className="ml-auto flex items-center gap-1">
        <ToolbarDivider />
        <ToolbarButton
          label="Undo (Cmd/Ctrl + Z)"
          disabled={!editor.can().undo()}
          onClick={() => editor.chain().focus().undo().run()}
        >
          <UndoIcon />
        </ToolbarButton>
        <ToolbarButton
          label="Redo (Cmd/Ctrl + Shift + Z)"
          disabled={!editor.can().redo()}
          onClick={() => editor.chain().focus().redo().run()}
        >
          <RedoIcon />
        </ToolbarButton>
        <ToolbarDivider />
        <ToolbarButton
          label="Switch to Markdown editing"
          onClick={onToggleMode}
        >
          <MarkdownIcon />
        </ToolbarButton>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// Editor
// -------------------------------------------------------------------

export function RichTextEditor({
  value,
  onChange,
  placeholder,
  minHeight = "8rem",
  id,
  ariaLabel,
  onUploadImage,
}: RichTextEditorProps): JSX.Element {
  // The paste handler captures this ref by identity, so the upload
  // callback is read at paste time (not at editor-init time) and React
  // can pass a new function on every render without invalidating the
  // editor instance.
  const uploadImageRef = useRef<RichTextEditorProps["onUploadImage"]>(
    onUploadImage,
  );
  uploadImageRef.current = onUploadImage;

  // Markdown mode state. ``mdSource`` is only meaningful while
  // mode === "markdown"; toggling into the mode re-derives it from
  // the current document. ``lastEmittedHtml`` distinguishes our own
  // onChange round-trip from an EXTERNAL value change (composer
  // cleared after submit, edit-seed swapped) - only the external case
  // may overwrite what the user is typing in the textarea.
  const [mode, setMode] = useState<ComposerMode>(readPreferredMode);
  const [mdSource, setMdSource] = useState<string>(() =>
    readPreferredMode() === "markdown" && value ? htmlToMarkdown(value) : "",
  );
  const lastEmittedHtmlRef = useRef<string>(value);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Replace the bundled Paragraph with our zero-margin variant
        // (see TightParagraph above). Everything else in StarterKit
        // stays default - bold, italic, lists, headings, etc.
        paragraph: false,
      }),
      TightParagraph,
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
      ResizableImage.configure({
        inline: true,
        allowBase64: false,
        HTMLAttributes: { class: "buyist-inline-image" },
      }),
      // Tables are not authored by agents in the toolbar today (no
      // Insert Table button); the reason they ship with the editor is
      // preservation. An AI agent posting via the public API can
      // include ``<table>`` in body_html (the "Draft from API key"
      // path); without these extensions ProseMirror's schema would
      // drop the table nodes on load and the agent would lose the
      // formatting the moment they clicked Edit. With the four
      // extensions loaded, the table round-trips through the editor.
      Table.configure({ resizable: false, HTMLAttributes: { class: "buyist-table" } }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: value,
    editorProps: {
      attributes: {
        // ``style`` here lands on the contenteditable .ProseMirror div
        // itself, so the inner editable fills the wrapper height. Without
        // this, an empty editor only renders 36px tall and clicks below
        // the first line miss the contenteditable.
        style: `min-height: ${minHeight}`,
        class:
          "prose prose-sm max-w-none px-3 py-2 focus:outline-none text-sm text-neutral-900",
        ...(id ? { id } : {}),
        ...(ariaLabel ? { "aria-label": ariaLabel } : {}),
        ...(placeholder ? { "data-placeholder": placeholder } : {}),
      },
      handlePaste(view, event) {
        // Intercept clipboard image files BEFORE ProseMirror's default
        // paste pipeline gets a chance to turn them into base64 data
        // URIs (which we never want stored on the server). The shared
        // helper uploads each image and inserts an ``<img>`` node at
        // the current selection.
        const handler = uploadImageRef.current;
        const files = event.clipboardData?.files ?? null;
        const handled = uploadAndInsertImages(view, files, handler);
        if (handled) event.preventDefault();
        return handled;
      },
      handleDrop(view, event, _slice, moved) {
        // Drag-drop inline images (added 2026-05-20 per Greg's
        // request). When the agent drags an image file from the
        // OS file picker / desktop / another tab into the body,
        // upload it and insert at the drop point - same end state
        // as a paste, just a different ingestion path.
        //
        // ``moved=true`` means ProseMirror is dragging content
        // around inside this same editor instance. We return false
        // there so the editor's native move logic runs (otherwise
        // dragging a paragraph would also try to upload it as an
        // image).
        if (moved) return false;
        const handler = uploadImageRef.current;
        const files = event.dataTransfer?.files ?? null;
        if (!files || files.length === 0) return false;
        // Find the position under the cursor at drop time. Fallback
        // to the current selection if posAtCoords returns null
        // (rare, but JSDOM and very-edge clicks can produce it).
        const coords = view.posAtCoords({
          left: event.clientX,
          top: event.clientY,
        });
        const dropPos = coords?.pos;
        const handled = uploadAndInsertImages(view, files, handler, dropPos);
        if (handled) {
          // preventDefault stops the browser from navigating to the
          // file URL; stopPropagation keeps a sibling AttachmentDropzone
          // from ALSO picking up the same file as a separate ticket-
          // level attachment. Both are needed.
          event.preventDefault();
          event.stopPropagation();
        }
        return handled;
      },
    },
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      lastEmittedHtmlRef.current = html;
      onChange(html, ed.getText());
    },
  });

  // Sync external resets (e.g. after a successful submit clears value).
  useEffect(() => {
    if (!editor) return;
    if (editor.getHTML() !== value) {
      editor.commands.setContent(value, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Markdown-mode counterpart of the reset sync: when the parent
  // swaps ``value`` to something we did not just emit (cleared after
  // submit, a different draft loaded), re-derive the textarea source.
  // Our own emissions round-trip back as ``value`` updates too; those
  // must NOT regenerate mdSource or the cursor-side text would churn
  // under the user's fingers.
  useEffect(() => {
    if (value === lastEmittedHtmlRef.current) return;
    lastEmittedHtmlRef.current = value;
    if (mode === "markdown") {
      setMdSource(value ? htmlToMarkdown(value) : "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, mode]);

  function handleMarkdownChange(src: string) {
    setMdSource(src);
    const html = src.trim() ? markdownToHtml(src) : "";
    lastEmittedHtmlRef.current = html;
    // The plain-text projection IS the markdown source - readable as
    // text by construction, and identical to what the public API
    // stores for body_markdown-only payloads.
    onChange(html, src);
  }

  function toggleMode() {
    if (mode === "rich") {
      const html = editor ? editor.getHTML() : value;
      setMdSource(htmlToMarkdown(html));
      setMode("markdown");
      persistPreferredMode("markdown");
    } else {
      // Seed the WYSIWYG from the markdown the user wrote. No
      // onChange here: the parent already holds the equivalent HTML
      // from the last keystroke emission; the editor re-serializes
      // on the next edit anyway.
      const html = mdSource.trim() ? markdownToHtml(mdSource) : "";
      editor?.commands.setContent(html, false);
      setMode("rich");
      persistPreferredMode("rich");
    }
  }

  return (
    <div className="rounded-lg border border-neutral-300 bg-white shadow-sm focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500">
      {editor && <Toolbar editor={editor} mode={mode} onToggleMode={toggleMode} />}
      {/* The WYSIWYG surface stays MOUNTED (display:none) in markdown
          mode - unmounting EditorContent while keeping the editor
          instance alive has view-reparenting edge cases in TipTap v2,
          and the hidden document doubles as the conversion target
          when toggling back. */}
      <div
        className={mode === "markdown" ? "hidden" : "cursor-text"}
        onClick={() => editor?.commands.focus()}
      >
        <EditorContent editor={editor} />
      </div>
      {mode === "markdown" && (
        <textarea
          ref={textareaRef}
          value={mdSource}
          onChange={(e) => handleMarkdownChange(e.target.value)}
          placeholder={placeholder}
          aria-label={ariaLabel ? `${ariaLabel} (Markdown)` : "Markdown source"}
          {...(id ? { id } : {})}
          data-testid="markdown-composer"
          spellCheck
          className="block w-full resize-y rounded-b-lg bg-white px-3 py-2 font-mono text-[13px] leading-relaxed text-neutral-900 placeholder:text-neutral-400 focus:outline-none"
          style={{ minHeight }}
        />
      )}
    </div>
  );
}

export default RichTextEditor;
